"use client";
import React, { useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { getCountryKey } from "helpers/country";

import JobDetail from "app/(new-pages)/components/JobDetail/HomeJobDetail/JobDetail.pc";
import JobCardPc from "app/(new-pages)/components/JobCard/pc/index.pc";
import CompleteProfileCard from "app/components/CompleteProfileCard";
import { LoadingContext } from "app/components/providers/loadingProvider";
import PageLoading from "app/(new-pages)/components/jobListCommon/Loading/Loading";
import { JobTransProvider } from "app/(new-pages)/components/JobDetail/jobTransProvider";
import JobCountTracker from "components/ReportTracker/JobCountTracker";
import {
  getDeviceNameTracker,
  jobCountSourceTracker,
  viewJobSourceTracker
} from "components/ReportTracker/util";
import Switch, { SwitchCardType } from "app/(new-pages)/components/Switch";
import JobLoginCard from "components/JobLoginCard/JobLoginCard";

import { fetchJobDetail } from "./service";

import Loading from "./Loading";
import Empty from "../../Empty";
import Pagination from "../../Pagination";

import styles from "../../../style/index.module.scss";
import { getCookie } from "helpers/cookies";
import { getDeviceUuid } from "helpers/guest";
import { addJobViewService } from "store/services/jobs/addJobView";

const TablePc = ({
  jobs,
  id,
  sessionid,
  dictionary,
  page,
  totalPages,
  job_search_empty,
  searchParams,
  config,
  is_profile_completed
}) => {
  const { cctip_lee = {} } = dictionary;
  const [detail, setDetail] = useState<any>(jobs[0]?.job_detail || {});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const countryKey = getCountryKey();
  const detailRef = useRef(null);
  const isLogin = getCookie("sessionid") ? true : false;
  const source = viewJobSourceTracker.remoteJobList;

  const addView = async ({ job_id, reco_from, query_id }) => {
    const device_udid = await getDeviceUuid();
    addJobViewService({
      jobId: job_id || null,
      source: source, // this is usually used in search result
      status: "public",
      device: getDeviceNameTracker(false), // pc
      device_udid,
      is_login: sessionid ? 1 : 0,
      reco_from: reco_from || null,
      query_id: query_id || null
    });
  };

  const [cardType, setCardType] = useState<SwitchCardType>("column");

  useEffect(() => {
    if (id) {
      setCurrentIndex(0);
      setDetail(jobs[0]?.job_detail || {});
      addView({
        job_id: jobs[0]?.job_id,
        reco_from: jobs[0]?.reco_from,
        query_id: jobs[0]?.query_id
      });
    }
  }, [jobs]);

  const fetchDetail = async (job_id) => {
    try {
      setLoading(true);
      const res = await fetchJobDetail({ job_id });
      setLoading(false);
      if (res?.data?.code == 0) {
        setDetail(res.data.data);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const changeDetail = (e, index) => {
    if (currentIndex === index) {
      return;
    }

    e.stopPropagation();
    fetchDetail(jobs[index].job_id);
    setCurrentIndex(index);
    addView({
      job_id: jobs[index]?.job_id,
      reco_from: jobs[index]?.reco_from,
      query_id: jobs[index]?.query_id
    });

    // Scroll contentRight back to top
    const rightContent = document.querySelector(".yolo-content-right");
    if (rightContent) {
      rightContent.scrollTop = 0;
    }
  };

  const { loading: listLoading } = useContext(LoadingContext);

  const getContentClassName = () =>
    classNames({
      [styles.content]: true,
      [styles.content_whiteBg]: !jobs?.length
    });

  const isEmpty = !jobs?.length;

  if (listLoading) {
    return (
      <div className={getContentClassName()}>
        <PageLoading />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={getContentClassName()}>
        <Empty
          job_search_empty={job_search_empty}
          searchParams={searchParams}
          config={config}
          dictionary={dictionary}
        />
      </div>
    );
  }

  const renderBlurredJob = () => (
    <JobCardPc
      item={jobs[0]}
      index={0}
      currentIndex={-1}
      changeDetail={null}
      cardType={cardType}
      blur={true}
    />
  );

  const jobsIds = (jobs || []).map((item) => item?.job_id).join(",");
  const reportTrackerData = {
    source: jobCountSourceTracker.remoteJobsHiring,
    job_ids: jobsIds
  };

  const listArr = [
    cardType === "row" && sessionid ? !is_profile_completed ? <CompleteProfileCard /> : null : null
  ]
    .concat(jobs)
    .filter((item) => item != null);

  return (
    <>
      <div className={styles.pageTitle}>
        <span>{cctip_lee?.jobList ?? "Job List"}</span>
        <Switch cardType={cardType} setCardType={setCardType} />
      </div>

      <div
        className={classNames({
          [styles.content_defaultBg]: true,
          [styles.content_whiteBg]: !jobs?.length
        })}
      >
        <div
          className={classNames({
            [styles.content]: true,
            // [styles.content_whiteBg]: !jobs?.length,
            [styles.listRow]: cardType === "row"
          })}
        >
          <JobTransProvider data={detail}>
            <div className={styles.contentLeft}>
              {sessionid && !is_profile_completed && <CompleteProfileCard cardType={cardType} />}

              {jobs?.map((item, index) => {
                return (
                  <JobCardPc
                    item={item}
                    key={item.id || index}
                    index={index}
                    currentIndex={currentIndex}
                    changeDetail={changeDetail}
                    cardType={cardType}
                    source={viewJobSourceTracker.remoteJobList}
                  />
                );
              })}

              {jobs.length > 0 && !isLogin ? (
                listArr.length % 2 === 0 || cardType === "column" ? (
                  <>
                    <JobLoginCard cardType={cardType} />
                    {renderBlurredJob()}
                  </>
                ) : (
                  <>
                    <JobLoginCard cardType={cardType} />
                    {renderBlurredJob()}
                    {renderBlurredJob()}
                  </>
                )
              ) : null}

              {isLogin && cardType === "column" && (
                <Pagination page={page} totalPages={totalPages} />
              )}
            </div>

            <div className={classNames(styles.contentRight, "yolo-content-right")}>
              {loading ? (
                <Loading />
              ) : detail && Object.keys(detail)?.length ? (
                <JobDetail
                  ref={detailRef}
                  countryKey={countryKey}
                  sessionid={sessionid}
                  jobId={detail?.job_id}
                  data={detail}
                />
              ) : null}
            </div>
          </JobTransProvider>

          <JobCountTracker data={reportTrackerData} />
        </div>

        {isLogin && cardType === "row" && totalPages > 1 && (
          <div className={styles.paginationWarp}>
            <Pagination page={page} totalPages={totalPages} />
          </div>
        )}
      </div>
    </>
  );
};

export default TablePc;
